import React, { Component } from "react";
import { AiChatbotCreditsPanelEmbeddable } from "../../../../management/settings/ai-chatbot/ai-chatbot-credits-panel";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";

interface McpChatBuyCreditsModalProps {
  onClose: () => void;
  /**
   * Fallback when the embedded credits panel can't mount in the designer
   * context. Should perform the legacy full-page redirect to the settings
   * credits route.
   */
  onEmbedFailed: () => void;
}

interface EmbedBoundaryProps {
  children: React.ReactNode;
  onError: () => void;
}

interface EmbedBoundaryState {
  hasError: boolean;
}

/**
 * Isolates the embedded credits panel: if it throws while mounting, we degrade
 * to the legacy redirect instead of crashing the chat panel. Error boundaries
 * must be class components.
 */
class CreditsEmbedBoundary extends Component<EmbedBoundaryProps, EmbedBoundaryState> {
  state: EmbedBoundaryState = { hasError: false };

  static getDerivedStateFromError(): EmbedBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(): void {
    // Degrade to the full-page settings route once the render has failed.
    this.props.onError();
  }

  render(): React.ReactElement | null {
    if (this.state.hasError) return null;
    return this.props.children as React.ReactElement;
  }
}

/**
 * Opens the existing credits UI inside a KModal instead of a full-page redirect
 * to `/{domain}/settings/ai-chatbot-credits`. Same component, in-context.
 *
 * Caveat: Stripe checkout still navigates away (window.location) — that is
 * inherent to Stripe and unchanged; the modal only makes the ENTRY point
 * in-context. The panel's session_id return handling is unchanged.
 */
const McpChatBuyCreditsModal: React.FC<McpChatBuyCreditsModalProps> = ({ onClose, onEmbedFailed }) => {
  const handleEmbedFailed = (): void => {
    onClose();
    onEmbedFailed();
  };

  return (
    <KModal
      showModal={true}
      closeModal={onClose}
      header="Buy credits"
      width={780}
      centered
      showFullScreenButton
      dontRenderSpinner
      className="mcp-chat__buy-credits-modal"
    >
      <div className="mcp-chat__buy-credits-modal-body kuika-settings-panel">
        <CreditsEmbedBoundary onError={handleEmbedFailed}>
          {React.createElement(AiChatbotCreditsPanelEmbeddable as unknown as React.ComponentType)}
        </CreditsEmbedBoundary>
      </div>
    </KModal>
  );
};

export default McpChatBuyCreditsModal;
